import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Bold,
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
  Italic,
  Underline,
} from "lucide-react";
import { useCallback, useState } from "react";
import { useMatterJurisdiction } from "@/components/contexts/MatterJurisdictionContext";
import { useMatterLabels } from "@/lib/useMatterLabels";
import { SimpleColorPickerGrid } from "@/components/shared/SimpleColorPickerGrid";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/NewButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { isSpreadsheetBundleExtension } from "./bundleEligibility";
import { useBundleTreeStore } from "./bundleTreeStore";
import {
  type BundleMetadata,
  DATE_FORMAT_OPTIONS,
  type FontOption,
  type FontStyle,
  GroupType,
  INDEX_COLUMN_LABELS,
  type SpreadsheetGridlines,
  type SpreadsheetHeadings,
  type SpreadsheetOrientation,
  type SpreadsheetOverflow,
  type SpreadsheetSettings,
  volumeStampLabel,
} from "./bundleTypes";

// Text colors: each row is shades of one colour (dark → light)
const TEXT_COLORS = [
  '#000000', '#333333', '#555555', '#777777', '#999999', '#BBBBBB',
  '#4A0000', '#8B0000', '#B71C1C', '#D32F2F', '#E57373', '#FFCDD2',
  '#BF360C', '#E65100', '#F57C00', '#FB8C00', '#FFB74D', '#FFE0B2',
  '#1B5E20', '#2E7D32', '#388E3C', '#4CAF50', '#81C784', '#C8E6C9',
  '#0D47A1', '#1565C0', '#1976D2', '#2196F3', '#64B5F6', '#BBDEFB',
  '#4A148C', '#6A1B9A', '#7B1FA2', '#9C27B0', '#BA68C8', '#E1BEE7',
];

// Background colors: lighter/pastel shades suitable for backgrounds
const BG_COLORS = [
  '#FFFFFF', '#F5F5F5', '#EEEEEE', '#E0E0E0', '#BDBDBD', '#9E9E9E',
  '#FFF3E0', '#FFE0B2', '#FFCC80', '#FFE082', '#FFF9C4', '#FFFDE7',
  '#E8F5E9', '#C8E6C9', '#A5D6A7', '#DCEDC8', '#F1F8E9', '#F9FBE7',
  '#E3F2FD', '#BBDEFB', '#90CAF9', '#B3E5FC', '#E1F5FE', '#E0F7FA',
  '#F3E5F5', '#E1BEE7', '#CE93D8', '#F8BBD0', '#FCE4EC', '#FFF0F5',
  '#EFEBE9', '#D7CCC8', '#BCAAA4', '#CFD8DC', '#ECEFF1', '#F5F5F5',
];

function joinBatesPreview(prefix: string, volume: string, number: string): string {
  const chunks: string[] = [];
  const cleanedPrefix = prefix.trim().replace(/-+$/, '');
  if (cleanedPrefix) chunks.push(cleanedPrefix);
  if (volume) chunks.push(volume);
  chunks.push(number);
  return chunks.join('-');
}

type BundleSettingsPanelProps = {
  settings: BundleMetadata;
  onChange: (metadata: BundleMetadata) => void;
  fontOptions: FontOption[];
  fontCssMap: Record<string, string>;
};

export const BundleSettingsPanel = ({
  settings,
  onChange,
  fontOptions,
  fontCssMap,
}: BundleSettingsPanelProps) => {
  const { ui_labels } = useMatterJurisdiction().jurisdiction;
  const hasSpreadsheet = useBundleTreeStore((s) =>
    Object.values(s.config.files).some(
      (file) => file.includedInBundle !== false && isSpreadsheetBundleExtension(file.fileExtension),
    ),
  );
  const firstVolumeTitle = useBundleTreeStore((s) => {
    for (const id of s.config.rootChildIds) {
      const group = s.config.groups[id];
      if (group?.groupType === GroupType.VOLUME) return group.title;
    }
    return '';
  });
  const [openSection, setOpenSection] = useState<string>('');
  const toggleSection = useCallback((id: string) => {
    setOpenSection((prev) => (prev === id ? '' : id));
  }, []);
  const [activeColorPicker, setActiveColorPicker] = useState<string | null>(null);

  const moveColumn = useCallback((index: number, direction: -1 | 1) => {
    const cols = [...settings.index_columns];
    const target = index + direction;
    if (target < 0 || target >= cols.length) return;
    [cols[index], cols[target]] = [cols[target], cols[index]];
    onChange({ ...settings, index_columns: cols });
  }, [onChange, settings]);

  const toggleColumn = useCallback((index: number) => {
    const cols = [...settings.index_columns];
    cols[index] = { ...cols[index], visible: !cols[index].visible };
    onChange({ ...settings, index_columns: cols });
  }, [onChange, settings]);

  const spreadsheet = settings.spreadsheet;
  const patchSpreadsheet = useCallback((updates: Partial<SpreadsheetSettings>) => {
    onChange({ ...settings, spreadsheet: { ...settings.spreadsheet, ...updates } });
  }, [onChange, settings]);
  const patchPageNumbers = useCallback((updates: Record<string, unknown>) => {
    onChange({ ...settings, page_numbers: { ...settings.page_numbers, ...updates } });
  }, [onChange, settings]);
  const patchBates = useCallback((updates: Record<string, unknown>) => {
    onChange({ ...settings, bates_stamp: { ...settings.bates_stamp, ...updates } });
  }, [onChange, settings]);

  const batesPreviewNumber = String(settings.bates_stamp.start_number).padStart(settings.bates_stamp.num_digits, '0');
  const batesVolumeLabel = volumeStampLabel(firstVolumeTitle)
    || (settings.volume_naming === 'numeric' ? '1' : 'A');
  const batesPreviewText = settings.bates_stamp.volume_prefix
    ? joinBatesPreview(settings.bates_stamp.prefix, batesVolumeLabel, batesPreviewNumber)
      + settings.bates_stamp.suffix
    : `${settings.bates_stamp.prefix}${batesPreviewNumber}${settings.bates_stamp.suffix}`;

  return (
    <div className="space-y-2" onClick={() => activeColorPicker && setActiveColorPicker(null)}>
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">General</h3>
      <div className="flex items-start gap-3 px-3 py-2 bg-gray-50 rounded-lg">
        <Checkbox
          id="remove-blank-pages"
          checked={settings.remove_blank_pages}
          onCheckedChange={(checked) => onChange({ ...settings, remove_blank_pages: checked === true })}
          className="mt-0.5"
        />
        <div className="grid gap-1 leading-none">
          <label htmlFor="remove-blank-pages" className="text-sm font-medium text-gray-700 cursor-pointer">
            Remove Blank Pages
          </label>
          <p className="text-xs text-gray-500">
            Automatically remove pages with no visible content during bundle generation.
          </p>
        </div>
      </div>

      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mt-4">Volumes</h3>
      <div className="flex items-start gap-3 px-3 py-2 bg-gray-50 rounded-lg">
        <Checkbox
          id="volume-divider-page"
          checked={settings.volume_divider_page}
          onCheckedChange={(checked) => onChange({ ...settings, volume_divider_page: checked === true })}
          className="mt-0.5"
        />
        <div className="grid gap-1 leading-none">
          <label htmlFor="volume-divider-page" className="text-sm font-medium text-gray-700 cursor-pointer">
            Volume Divider Page
          </label>
          <p className="text-xs text-gray-500">
            Insert a divider page at the start of each volume showing the volume name.
          </p>
        </div>
      </div>

      <div className="flex items-start gap-3 px-3 py-2 bg-gray-50 rounded-lg">
        <Checkbox
          id="per-volume-index"
          checked={settings.per_volume_index}
          onCheckedChange={(checked) => onChange({ ...settings, per_volume_index: checked === true })}
          className="mt-0.5"
        />
        <div className="grid gap-1 leading-none">
          <label htmlFor="per-volume-index" className="text-sm font-medium text-gray-700 cursor-pointer">
            Per-Volume Index
          </label>
          <p className="text-xs text-gray-500">
            Each volume begins with its own index listing only the documents in that volume.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg">
        <div className="grid gap-1 leading-none">
          <span className="text-sm font-medium text-gray-700">Volume Naming</span>
          <p className="text-xs text-gray-500">
            Label volumes alphabetically (A, B, C) or numerically (1, 2, 3).
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          <ToggleBtn
            active={settings.volume_naming === 'alpha'}
            onClick={() => onChange({ ...settings, volume_naming: 'alpha' })}
            title="Alphabetic (A, B, C...)"
            compact
          >
            <span className="text-xs font-semibold">A, B, C</span>
          </ToggleBtn>
          <ToggleBtn
            active={settings.volume_naming === 'numeric'}
            onClick={() => onChange({ ...settings, volume_naming: 'numeric' })}
            title="Numeric (1, 2, 3...)"
            compact
          >
            <span className="text-xs font-semibold">1, 2, 3</span>
          </ToggleBtn>
        </div>
      </div>

      {/* Volume divider page title */}
      <Collapsible open={openSection === 'divider'} onOpenChange={() => toggleSection('divider')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'divider' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Divider Page Title
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <TextStyleEditor
              style={settings.volume_divider_title}
              onChange={(updates) => onChange({ ...settings, volume_divider_title: { ...settings.volume_divider_title, ...updates } })}
              activeColorPicker={activeColorPicker}
              onColorPickerChange={setActiveColorPicker}
              colorPickerPrefix="divider"
              previewText={settings.volume_naming === 'numeric' ? 'Volume 1' : 'Volume A'}
              fontOptions={fontOptions}
              fontCssMap={fontCssMap}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mt-4">Index</h3>
      {/* Layout */}
      <Collapsible open={openSection === 'layout'} onOpenChange={() => toggleSection('layout')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'layout' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Layout
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <div className="space-y-1">
              <span className="text-sm text-gray-600">Columns</span>
              <div className="border rounded-lg divide-y">
                {settings.index_columns.map((col, i) => (
                  <div key={col.key} className="flex items-center gap-2 px-3 py-1.5">
                    <div className="flex flex-col">
                      <Button
                        type="button"
                        icon={ArrowUp}
                        variant="bare"
                        size="icon-xs"
                        onClick={() => moveColumn(i, -1)}
                        disabled={i === 0}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-25"
                      />
                      <Button
                        type="button"
                        icon={ArrowDown}
                        variant="bare"
                        size="icon-xs"
                        onClick={() => moveColumn(i, 1)}
                        disabled={i === settings.index_columns.length - 1}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-25"
                      />
                    </div>
                    <span className={`text-sm flex-1 ${col.visible ? 'text-gray-700' : 'text-gray-400 line-through'}`}>
                      {INDEX_COLUMN_LABELS[col.key]}
                    </span>
                    <Button
                      type="button"
                      icon={col.visible ? Eye : EyeOff}
                      variant="bare"
                      size="icon-sm"
                      onClick={() => toggleColumn(i)}
                      className={col.visible ? 'text-gray-600 hover:text-gray-800' : 'text-gray-300 hover:text-gray-500'}
                    />
                  </div>
                ))}
              </div>
            </div>

            <SettingRow label="Date Format">
              <Select value={settings.date_format} onValueChange={(v) => onChange({ ...settings, date_format: v })}>
                <SelectTrigger className="h-8 text-sm w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DATE_FORMAT_OPTIONS.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      <span className="text-sm">{f.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </SettingRow>

            <div className="flex items-start gap-3">
              <Checkbox
                id="index-on-title-page"
                checked={settings.index_on_title_page}
                onCheckedChange={(checked) => onChange({ ...settings, index_on_title_page: checked === true })}
                className="mt-0.5"
              />
              <div className="grid gap-1 leading-none">
                <label htmlFor="index-on-title-page" className="text-sm text-gray-600 cursor-pointer">
                  Start Index On Title Page
                </label>
                <p className="text-xs text-gray-500">
                  The index table will begin on the title page below the {ui_labels.bundle_singular.toLowerCase()} title instead of on a separate page. Only applies if there is enough space on the title page.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Checkbox
                id="show-email-attachments-in-index"
                checked={settings.show_email_attachments_in_index}
                onCheckedChange={(checked) => onChange({ ...settings, show_email_attachments_in_index: checked === true })}
                className="mt-0.5"
              />
              <div className="grid gap-1 leading-none">
                <label htmlFor="show-email-attachments-in-index" className="text-sm text-gray-600 cursor-pointer">
                  Show Email Attachments In Index
                </label>
                <p className="text-xs text-gray-500">
                  Each email attachment is listed as an indented sub-row under its email in the index. When disabled, only the email appears in the index.
                </p>
              </div>
            </div>

          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Title */}
      <Collapsible open={openSection === 'indexTitle'} onOpenChange={() => toggleSection('indexTitle')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'indexTitle' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Title
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="index-title-enabled"
                checked={settings.index_title.enabled}
                onCheckedChange={(checked) => onChange({ ...settings, index_title: { ...settings.index_title, enabled: checked === true } })}
                className="mt-0.5"
              />
              <label htmlFor="index-title-enabled" className="text-sm text-gray-600 cursor-pointer">
                Enabled
              </label>
            </div>
            {settings.index_title.enabled && (
              <>
                <SettingRow label="Text">
                  <Input
                    value={settings.index_title.text}
                    onChange={(e) => onChange({ ...settings, index_title: { ...settings.index_title, text: e.target.value } })}
                    placeholder="Index"
                    className="h-8 text-sm w-48"
                  />
                </SettingRow>
                <TextStyleEditor
                  style={settings.index_title}
                  onChange={(updates) => onChange({ ...settings, index_title: { ...settings.index_title, ...updates } })}
                  activeColorPicker={activeColorPicker}
                  onColorPickerChange={setActiveColorPicker}
                  colorPickerPrefix="idxtitle"
                  previewText={settings.index_title.text || 'Index'}
                  fontOptions={fontOptions}
                  fontCssMap={fontCssMap}
                />
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Column Headers */}
      <Collapsible open={openSection === 'columnHeaders'} onOpenChange={() => toggleSection('columnHeaders')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'columnHeaders' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Column Headers
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <TextStyleEditor
              style={settings.column_headers}
              onChange={(updates) => onChange({ ...settings, column_headers: { ...settings.column_headers, ...updates } })}
              activeColorPicker={activeColorPicker}
              onColorPickerChange={setActiveColorPicker}
              colorPickerPrefix="colhdr"
              previewText="Tab  Document Description  Date  Page Ref"
              fontOptions={fontOptions}
              fontCssMap={fontCssMap}
              showBackground
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Volume Heading */}
      <Collapsible open={openSection === 'volume'} onOpenChange={() => toggleSection('volume')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'volume' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Volume Heading
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <TextStyleEditor
              style={settings.volume_heading}
              onChange={(updates) => onChange({ ...settings, volume_heading: { ...settings.volume_heading, ...updates } })}
              activeColorPicker={activeColorPicker}
              onColorPickerChange={setActiveColorPicker}
              colorPickerPrefix="vol"
              previewText={settings.volume_naming === 'numeric' ? 'Volume 1' : 'Volume A'}
              fontOptions={fontOptions}
              fontCssMap={fontCssMap}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Section Heading */}
      <Collapsible open={openSection === 'section'} onOpenChange={() => toggleSection('section')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'section' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Section Heading
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <TextStyleEditor
              style={settings.section_heading}
              onChange={(updates) => onChange({ ...settings, section_heading: { ...settings.section_heading, ...updates } })}
              activeColorPicker={activeColorPicker}
              onColorPickerChange={setActiveColorPicker}
              colorPickerPrefix="sec"
              previewText="Witness Statements"
              fontOptions={fontOptions}
              fontCssMap={fontCssMap}
              showBackground
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Document Row */}
      <Collapsible open={openSection === 'document'} onOpenChange={() => toggleSection('document')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'document' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Document Row
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <TextStyleEditor
              style={settings.document_row}
              onChange={(updates) => onChange({ ...settings, document_row: { ...settings.document_row, ...updates } })}
              activeColorPicker={activeColorPicker}
              onColorPickerChange={setActiveColorPicker}
              colorPickerPrefix="doc"
              previewText="First Witness Statement of John Smith"
              fontOptions={fontOptions}
              fontCssMap={fontCssMap}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>

      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mt-4">Page Stamps</h3>
      {/* Page Numbers */}
      <Collapsible open={openSection === 'pageNum'} onOpenChange={() => toggleSection('pageNum')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'pageNum' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Page Numbers
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="page-numbers-enabled"
                checked={settings.page_numbers.enabled}
                onCheckedChange={(checked) => onChange({ ...settings, page_numbers: { ...settings.page_numbers, enabled: checked === true } })}
                className="mt-0.5"
              />
              <label htmlFor="page-numbers-enabled" className="text-sm text-gray-600 cursor-pointer">
                Enabled
              </label>
            </div>
            {settings.page_numbers.enabled && (
              <>
                <OptionCheckbox
                  id="page-numbers-skip-title"
                  checked={settings.page_numbers.skip_title_page === true}
                  onCheckedChange={(checked) => patchPageNumbers({ skip_title_page: checked })}
                  label="Skip title page"
                  description="Leave the title page unnumbered."
                />
                <OptionCheckbox
                  id="page-numbers-skip-divider"
                  checked={settings.page_numbers.skip_volume_divider === true}
                  onCheckedChange={(checked) => patchPageNumbers({ skip_volume_divider: checked })}
                  label="Skip volume divider"
                  description="Leave volume divider pages unnumbered."
                />
                <OptionCheckbox
                  id="page-numbers-skip-main-index"
                  checked={settings.page_numbers.skip_main_index === true}
                  onCheckedChange={(checked) => patchPageNumbers({ skip_main_index: checked })}
                  label="Skip main index"
                  description="Leave the main (front) index pages unnumbered."
                />
                <OptionCheckbox
                  id="page-numbers-skip-volume-index"
                  checked={settings.page_numbers.skip_volume_index === true}
                  onCheckedChange={(checked) => patchPageNumbers({ skip_volume_index: checked })}
                  label="Skip volume index"
                  description="Leave the per-volume index pages unnumbered."
                />
                <OptionCheckbox
                  id="page-numbers-roman-index"
                  checked={settings.page_numbers.roman_index === true}
                  onCheckedChange={(checked) => patchPageNumbers({ roman_index: checked })}
                  label="Paginate index in Roman numerals"
                  description="Number the index, title page, and volume dividers i, ii, iii instead of 1, 2, 3."
                />
                <OptionCheckbox
                  id="page-numbers-index-per-volume"
                  checked={settings.page_numbers.index_per_volume === true}
                  onCheckedChange={(checked) => patchPageNumbers({ index_per_volume: checked })}
                  label="Paginate index per volume"
                  description="Restart index numbering at the start of each volume."
                />
                <OptionCheckbox
                  id="page-numbers-documents-per-volume"
                  checked={settings.page_numbers.document_per_volume === true}
                  onCheckedChange={(checked) => patchPageNumbers({ document_per_volume: checked })}
                  label="Paginate documents per volume"
                  description="Restart document page numbers at 1 in each volume."
                />
                <TextStyleEditor
                  style={settings.page_numbers}
                  onChange={(updates) => patchPageNumbers(updates)}
                  activeColorPicker={activeColorPicker}
                  onColorPickerChange={setActiveColorPicker}
                  colorPickerPrefix="pn"
                  previewText={settings.page_numbers.roman_index === true ? 'i' : '42'}
                  fontOptions={fontOptions}
                  fontCssMap={fontCssMap}
                  showPlacement
                />
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Bates Stamp */}
      <Collapsible open={openSection === 'bates'} onOpenChange={() => toggleSection('bates')}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
          {openSection === 'bates' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          Bates Stamp
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-4 py-3 space-y-3">
            <div className="flex items-start gap-3">
              <Checkbox
                id="bates-stamp-enabled"
                checked={settings.bates_stamp.enabled}
                onCheckedChange={(checked) => onChange({ ...settings, bates_stamp: { ...settings.bates_stamp, enabled: checked === true } })}
                className="mt-0.5"
              />
              <label htmlFor="bates-stamp-enabled" className="text-sm text-gray-600 cursor-pointer">
                Enabled
              </label>
            </div>
            {settings.bates_stamp.enabled && (
              <>
                <OptionCheckbox
                  id="bates-skip-title"
                  checked={settings.bates_stamp.skip_title_page === true}
                  onCheckedChange={(checked) => patchBates({ skip_title_page: checked })}
                  label="Skip title page"
                  description="Do not stamp the title page."
                />
                <OptionCheckbox
                  id="bates-skip-divider"
                  checked={settings.bates_stamp.skip_volume_divider === true}
                  onCheckedChange={(checked) => patchBates({ skip_volume_divider: checked })}
                  label="Skip volume divider"
                  description="Do not stamp volume divider pages."
                />
                <OptionCheckbox
                  id="bates-skip-main-index"
                  checked={settings.bates_stamp.skip_main_index === true}
                  onCheckedChange={(checked) => patchBates({ skip_main_index: checked })}
                  label="Skip main index"
                  description="Do not stamp the main (front) index pages."
                />
                <OptionCheckbox
                  id="bates-skip-volume-index"
                  checked={settings.bates_stamp.skip_volume_index === true}
                  onCheckedChange={(checked) => patchBates({ skip_volume_index: checked })}
                  label="Skip volume index"
                  description="Do not stamp the per-volume index pages."
                />
                <OptionCheckbox
                  id="bates-roman-index"
                  checked={settings.bates_stamp.roman_index === true}
                  onCheckedChange={(checked) => patchBates({ roman_index: checked })}
                  label="Paginate index in Roman numerals"
                  description="Stamp the index, title page, and volume dividers i, ii, iii instead of 1, 2, 3."
                />
                <OptionCheckbox
                  id="bates-index-per-volume"
                  checked={settings.bates_stamp.index_per_volume === true}
                  onCheckedChange={(checked) => patchBates({ index_per_volume: checked })}
                  label="Paginate index per volume"
                  description="Restart index stamp numbers at the start of each volume."
                />
                <OptionCheckbox
                  id="bates-documents-per-volume"
                  checked={settings.bates_stamp.document_per_volume === true}
                  onCheckedChange={(checked) => patchBates({ document_per_volume: checked })}
                  label="Paginate documents per volume"
                  description="Restart document stamp numbers at the start of each volume."
                />
                <OptionCheckbox
                  id="bates-volume-prefix"
                  checked={settings.bates_stamp.volume_prefix === true}
                  onCheckedChange={(checked) => patchBates({ volume_prefix: checked })}
                  label="Include volume name"
                  description='Add the volume title before the stamp, dropping a leading "Volume". Joined as prefix-title-number, for example HL-A-001.'
                />
                <TextStyleEditor
                  style={settings.bates_stamp}
                  onChange={(updates) => patchBates(updates)}
                  activeColorPicker={activeColorPicker}
                  onColorPickerChange={setActiveColorPicker}
                  colorPickerPrefix="bates"
                  previewText={batesPreviewText}
                  fontOptions={fontOptions}
                  fontCssMap={fontCssMap}
                  showPlacement
                  hidePreview
                />
                <SettingRow label="Prefix">
                  <Input
                    value={settings.bates_stamp.prefix}
                    onChange={(e) => patchBates({ prefix: e.target.value })}
                    className="h-8 text-sm w-32"
                    placeholder="e.g. ABC-"
                  />
                </SettingRow>
                <SettingRow label="Suffix">
                  <Input
                    value={settings.bates_stamp.suffix}
                    onChange={(e) => patchBates({ suffix: e.target.value })}
                    className="h-8 text-sm w-32"
                    placeholder="e.g. -DOC"
                  />
                </SettingRow>
                <SettingRow label="Start Number">
                  <Input
                    type="number"
                    min={0}
                    value={settings.bates_stamp.start_number}
                    onChange={(e) => patchBates({ start_number: parseInt(e.target.value) || 1 })}
                    className="h-8 text-sm w-20"
                  />
                </SettingRow>
                <SettingRow label="Digits">
                  <Input
                    type="number"
                    min={3}
                    max={15}
                    value={settings.bates_stamp.num_digits}
                    onChange={(e) => patchBates({ num_digits: parseInt(e.target.value) || 6 })}
                    className="h-8 text-sm w-20"
                  />
                </SettingRow>
                <StylePreview
                  style={settings.bates_stamp}
                  previewText={batesPreviewText}
                  fontCssMap={fontCssMap}
                  showPlacement
                />
              </>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {hasSpreadsheet && (
        <>
          <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1 mt-4">Spreadsheets</h3>
          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <div className="grid gap-1 leading-none">
              <span className="text-sm font-medium text-gray-700">Orientation</span>
              <p className="text-xs text-gray-500">
                Fit each sheet to A4. Auto picks portrait or landscape for the larger type.
              </p>
            </div>
            <Select
              value={spreadsheet.orientation}
              onValueChange={(v) => patchSpreadsheet({ orientation: v as SpreadsheetOrientation })}
            >
              <SelectTrigger className="h-8 text-sm w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto"><span className="text-sm">Auto</span></SelectItem>
                <SelectItem value="portrait"><span className="text-sm">Portrait</span></SelectItem>
                <SelectItem value="landscape"><span className="text-sm">Landscape</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <div className="grid gap-1 leading-none">
              <span className="text-sm font-medium text-gray-700">Column Text Overflow</span>
              <p className="text-xs text-gray-500">
                Clip cuts at the column edge. Auto-fit widens columns, then wraps leftovers. Wrap keeps widths and grows rows.
              </p>
            </div>
            <Select
              value={spreadsheet.overflow}
              onValueChange={(v) => patchSpreadsheet({ overflow: v as SpreadsheetOverflow })}
            >
              <SelectTrigger className="h-8 text-sm w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="clip"><span className="text-sm">Clip</span></SelectItem>
                <SelectItem value="autofit"><span className="text-sm">Auto-fit</span></SelectItem>
                <SelectItem value="wrap"><span className="text-sm">Wrap</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <div className="grid gap-1 leading-none">
              <span className="text-sm font-medium text-gray-700">Gridlines</span>
              <p className="text-xs text-gray-500">
                Print faint grey lines around cells with data. Author-drawn borders always print.
              </p>
            </div>
            <Select
              value={spreadsheet.gridlines}
              onValueChange={(v) => patchSpreadsheet({ gridlines: v as SpreadsheetGridlines })}
            >
              <SelectTrigger className="h-8 text-sm w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on"><span className="text-sm">On</span></SelectItem>
                <SelectItem value="off"><span className="text-sm">Off</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 rounded-lg">
            <div className="grid gap-1 leading-none">
              <span className="text-sm font-medium text-gray-700">Row and column headings</span>
              <p className="text-xs text-gray-500">
                Print column letters and row numbers.
              </p>
            </div>
            <Select
              value={spreadsheet.headings}
              onValueChange={(v) => patchSpreadsheet({ headings: v as SpreadsheetHeadings })}
            >
              <SelectTrigger className="h-8 text-sm w-32 shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="on"><span className="text-sm">On</span></SelectItem>
                <SelectItem value="off"><span className="text-sm">Off</span></SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Collapsible open={openSection === 'sheetName'} onOpenChange={() => toggleSection('sheetName')}>
            <CollapsibleTrigger className="flex items-center gap-2 w-full px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
              {openSection === 'sheetName' ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Sheet Name
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="px-4 py-3 space-y-3">
                <p className="text-xs text-gray-500">
                  Stamp the sheet name on each page of multi-sheet workbooks.
                </p>
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="sheet-name-enabled"
                    checked={spreadsheet.sheet_name.enabled}
                    onCheckedChange={(checked) => patchSpreadsheet({
                      sheet_name: { ...spreadsheet.sheet_name, enabled: checked === true },
                    })}
                    className="mt-0.5"
                  />
                  <label htmlFor="sheet-name-enabled" className="text-sm text-gray-600 cursor-pointer">
                    Enabled
                  </label>
                </div>
                {spreadsheet.sheet_name.enabled && (
                  <TextStyleEditor
                    style={spreadsheet.sheet_name}
                    onChange={(updates) => patchSpreadsheet({
                      sheet_name: { ...spreadsheet.sheet_name, ...updates },
                    })}
                    activeColorPicker={activeColorPicker}
                    onColorPickerChange={setActiveColorPicker}
                    colorPickerPrefix="sheet"
                    previewText="Sheet 1"
                    fontOptions={fontOptions}
                    fontCssMap={fontCssMap}
                    showPlacement
                  />
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        </>
      )}
    </div>
  );
};

// --- Reusable text style editor ---

type StylePreviewProps = {
  style: FontStyle & { background?: string; placement?: 'top' | 'bottom' };
  previewText: string;
  fontCssMap: Record<string, string>;
  showPlacement?: boolean;
};

const StylePreview = ({ style, previewText, fontCssMap, showPlacement = false }: StylePreviewProps) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-stretch">
    <div className="md:col-span-1 flex items-center text-sm font-medium text-gray-600 px-1 py-2">
      Preview
    </div>
    <div
      className={`md:col-span-2 border rounded-sm px-3 ${showPlacement ? 'py-1 flex flex-col justify-center min-h-[48px]' : 'py-2 flex items-center'}`}
      style={{ backgroundColor: style.background || undefined }}
    >
      {showPlacement && style.placement === 'bottom' && <div className="flex-1" />}
      <span
        style={{
          fontFamily: fontCssMap[style.font_family] || "sans-serif",
          fontSize: `${style.font_size}px`,
          color: style.color,
          fontWeight: style.bold ? "bold" : "normal",
          fontStyle: style.italic ? "italic" : "normal",
          textDecoration: style.underline ? "underline" : "none",
          textAlign: style.alignment,
          display: "block",
          width: "100%",
          lineHeight: 1.3,
        }}
      >
        {previewText}
      </span>
      {showPlacement && style.placement === 'top' && <div className="flex-1" />}
    </div>
  </div>
);

type TextStyleEditorProps = {
  style: FontStyle & { background?: string; placement?: 'top' | 'bottom' };
  onChange: (updates: Record<string, unknown>) => void;
  activeColorPicker: string | null;
  onColorPickerChange: (id: string | null) => void;
  colorPickerPrefix: string;
  previewText: string;
  fontOptions: FontOption[];
  fontCssMap: Record<string, string>;
  showBackground?: boolean;
  showPlacement?: boolean;
  hidePreview?: boolean;
};

const TextStyleEditor = ({
  style,
  onChange,
  activeColorPicker,
  onColorPickerChange,
  colorPickerPrefix,
  previewText,
  fontOptions,
  fontCssMap,
  showBackground = false,
  showPlacement = false,
  hidePreview = false,
}: TextStyleEditorProps) => {
  const label = useMatterLabels();
  return (
  <>
    <SettingRow label="Font">
      <Select value={style.font_family} onValueChange={(v) => onChange({ font_family: v })}>
        <SelectTrigger className="h-8 text-sm w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="max-h-112">
          {fontOptions.map((f) => (
            <SelectItem key={f.value} value={f.value}>
              <span style={{
                fontFamily: f.fontFamily,
                fontWeight: style.bold ? "bold" : "normal",
                fontStyle: style.italic ? "italic" : "normal",
                textDecoration: style.underline ? "underline" : "none",
              }}>{f.label}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </SettingRow>

    <SettingRow label="Size">
      <Input
        type="number"
        min={8}
        max={72}
        value={style.font_size}
        onChange={(e) => onChange({ font_size: parseInt(e.target.value) || 16 })}
        className="h-8 text-sm w-20"
      />
    </SettingRow>

    <SettingRow label={label('colour')}>
      <SimpleColorPickerGrid compact
        selectedColor={style.color}
        onColorSelect={(color) => onChange({ color })}
        colors={TEXT_COLORS}
        isOpen={activeColorPicker === `${colorPickerPrefix}-color`}
        onOpenChange={(open) => onColorPickerChange(open ? `${colorPickerPrefix}-color` : null)}
      />
    </SettingRow>

    {showBackground && (
      <SettingRow label="Background">
        <SimpleColorPickerGrid compact
          selectedColor={style.background || '#FFFFFF'}
          onColorSelect={(color) => onChange({ background: color })}
          colors={BG_COLORS}
          isOpen={activeColorPicker === `${colorPickerPrefix}-bg`}
          onOpenChange={(open) => onColorPickerChange(open ? `${colorPickerPrefix}-bg` : null)}
        />
      </SettingRow>
    )}

    <SettingRow label="Style">
      <div className="flex gap-1">
        <ToggleBtn active={style.bold} onClick={() => onChange({ bold: !style.bold })} title="Bold">
          <Bold className="h-3.5 w-3.5" />
        </ToggleBtn>
        <ToggleBtn active={style.italic} onClick={() => onChange({ italic: !style.italic })} title="Italic">
          <Italic className="h-3.5 w-3.5" />
        </ToggleBtn>
        <ToggleBtn active={style.underline} onClick={() => onChange({ underline: !style.underline })} title="Underline">
          <Underline className="h-3.5 w-3.5" />
        </ToggleBtn>
      </div>
    </SettingRow>

    <SettingRow label="Align">
      <div className="flex gap-1">
        <ToggleBtn active={style.alignment === "left"} onClick={() => onChange({ alignment: "left" })} title="Left">
          <AlignLeft className="h-3.5 w-3.5" />
        </ToggleBtn>
        <ToggleBtn active={style.alignment === "center"} onClick={() => onChange({ alignment: "center" })} title="Center">
          <AlignCenter className="h-3.5 w-3.5" />
        </ToggleBtn>
        <ToggleBtn active={style.alignment === "right"} onClick={() => onChange({ alignment: "right" })} title="Right">
          <AlignRight className="h-3.5 w-3.5" />
        </ToggleBtn>
      </div>
    </SettingRow>

    {showPlacement && (
      <SettingRow label="Placement">
        <div className="flex gap-1">
          <ToggleBtn active={style.placement === 'top'} onClick={() => onChange({ placement: 'top' })} title="Top Of Page">
            <span className="text-xs font-semibold">Top</span>
          </ToggleBtn>
          <ToggleBtn active={style.placement === 'bottom'} onClick={() => onChange({ placement: 'bottom' })} title="Bottom Of Page">
            <span className="text-xs font-semibold">Bottom</span>
          </ToggleBtn>
        </div>
      </SettingRow>
    )}

    {!hidePreview && (
      <StylePreview
        style={style}
        previewText={previewText}
        fontCssMap={fontCssMap}
        showPlacement={showPlacement}
      />
    )}
  </>
  );
};

// --- Sub-components ---

const OptionCheckbox = ({
  id,
  checked,
  onCheckedChange,
  label,
  description,
}: {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  label: string;
  description: string;
}) => (
  <div className="flex items-start gap-3">
    <Checkbox
      id={id}
      checked={checked}
      onCheckedChange={(value) => onCheckedChange(value === true)}
      className="mt-0.5"
    />
    <div className="grid gap-1 leading-none">
      <label htmlFor={id} className="text-sm text-gray-600 cursor-pointer">
        {label}
      </label>
      <p className="text-xs text-gray-500">{description}</p>
    </div>
  </div>
);

const SettingRow = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex items-center justify-between">
    <span className="text-sm text-gray-600 w-28 shrink-0">{label}</span>
    {children}
  </div>
);

const ToggleBtn = ({
  active,
  onClick,
  title,
  children,
  compact = false,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  compact?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`${compact ? 'px-1.5 py-0.5 h-7 flex items-center' : 'p-1.5'} rounded-sm border transition-colors ${
      active
        ? "bg-crimson-600 text-white border-crimson-600"
        : "bg-white text-gray-500 border-gray-200 hover:bg-gray-50"
    }`}
  >
    {children}
  </button>
);
